import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import "./EnterpriseProfileSection.css";

const API_BASE = process.env.REACT_APP_API_URL || "";

const defaultForm = {
  website: "",
  company_name: "",
  model: "gpt-4o-mini",
  max_pages: 8,
  include_news: true,
  force_regenerate: false,
};

const expectedSectionKeys = [
  "company_overview",
  "business_model",
  "products_and_services",
  "target_customers",
  "industry_and_market",
  "competitive_positioning",
  "brand_voice_and_tone",
  "key_value_propositions",
  "sales_and_marketing",
  "operations_and_locations",
  "leadership_and_team",
  "recent_news_and_updates",
  "risks_and_compliance",
  "llm_response_guidelines",
];

const sectionLabels = {
  company_overview: "Company Overview",
  business_model: "Business Model",
  products_and_services: "Products and Services",
  target_customers: "Target Customers",
  industry_and_market: "Industry and Market",
  competitive_positioning: "Competitive Positioning",
  brand_voice_and_tone: "Brand Voice and Tone",
  key_value_propositions: "Key Value Propositions",
  sales_and_marketing: "Sales and Marketing",
  operations_and_locations: "Operations and Locations",
  leadership_and_team: "Leadership and Team",
  recent_news_and_updates: "Recent News and Updates",
  risks_and_compliance: "Risks and Compliance",
  llm_response_guidelines: "LLM Response Guidelines",
};

function formatLabel(key) {
  return sectionLabels[key] || String(key).replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function renderValue(value) {
  if (value === null || value === undefined || value === "") {
    return "Not publicly available";
  }

  if (Array.isArray(value)) {
    if (!value.length) return "Not publicly available";
    return (
      <ul className="mb-0 ps-3">
        {value.map((item, index) => (
          <li key={`${String(item).slice(0, 20)}-${index}`}>{renderInlineValue(item)}</li>
        ))}
      </ul>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (!entries.length) return "Not publicly available";
    return (
      <div className="d-flex flex-column gap-1">
        {entries.map(([key, item]) => (
          <div key={key}>
            <strong>{formatLabel(key)}:</strong> {renderInlineValue(item)}
          </div>
        ))}
      </div>
    );
  }

  return String(value);
}

function renderInlineValue(value) {
  if (value === null || value === undefined || value === "") {
    return "Not publicly available";
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function readConfiguration(data) {
  return data?.configuration || data?.data?.configuration || null;
}

function readProfile(data) {
  return data?.profile || data?.data?.profile || null;
}

function getErrorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function EnterpriseProfileSection({
  enterpriseId,
  enterpriseName = "",
  sectionNumber,
  defaultOpen = true,
  mode = "admin",
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [form, setForm] = useState(defaultForm);
  const [configuration, setConfiguration] = useState(null);
  const [profile, setProfile] = useState(null);
  const [warning, setWarning] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isSuperAdmin = mode === "superadmin";
  const normalizedEnterpriseId = useMemo(
    () => String(enterpriseId || "").trim(),
    [enterpriseId],
  );

  const apiPaths = useMemo(() => {
    if (isSuperAdmin) {
      if (!normalizedEnterpriseId) {
        return { current: "", generate: "", delete: "" };
      }
      const base = `${API_BASE}/enterprise-profile/by-enterprise/${normalizedEnterpriseId}`;
      return {
        current: base,
        generate: `${base}/generate`,
        delete: base,
      };
    }

    return {
      current: `${API_BASE}/enterprise-profile/current`,
      generate: `${API_BASE}/enterprise-profile/generate`,
      delete: `${API_BASE}/enterprise-profile/current`,
    };
  }, [isSuperAdmin, normalizedEnterpriseId]);

  const updateField = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyConfigurationToForm = (config) => {
    if (!config) return;
    setForm((prev) => ({
      ...prev,
      website: config.website || prev.website,
      company_name: config.company_name || prev.company_name,
      model: config.model || prev.model,
      max_pages: config.max_pages || prev.max_pages,
      include_news:
        typeof config.include_news === "boolean"
          ? config.include_news
          : prev.include_news,
    }));
  };

  const validateSuperAdminEnterprise = () => {
    if (isSuperAdmin && !normalizedEnterpriseId) {
      showErrorToast("Enterprise ID missing");
      return false;
    }
    return true;
  };

  const validateGenerateForm = () => {
    if (!validateSuperAdminEnterprise()) return false;
    if (!String(form.website || "").trim()) {
      showErrorToast("Official Website is required");
      return false;
    }
    if (!String(form.company_name || "").trim()) {
      showErrorToast("Company Name is required");
      return false;
    }
    const maxPages = Number.parseInt(String(form.max_pages), 10);
    if (!Number.isFinite(maxPages) || maxPages < 1 || maxPages > 50) {
      showErrorToast("Maximum Pages must be between 1 and 50");
      return false;
    }
    return true;
  };

  const buildGeneratePayload = (forceRegenerate) => ({
    website: String(form.website || "").trim(),
    company_name: String(form.company_name || "").trim(),
    model: String(form.model || defaultForm.model).trim(),
    max_pages: Number.parseInt(String(form.max_pages), 10),
    include_news: Boolean(form.include_news),
    force_regenerate: Boolean(forceRegenerate),
  });

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      if (isSuperAdmin && !normalizedEnterpriseId) return;

      setIsLoading(true);
      setWarning(null);
      try {
        const res = await api.get(apiPaths.current, {
          silent: true,
          skipLoader: true,
        });
        if (!isMounted) return;

        const config = readConfiguration(res.data);
        const loadedProfile = readProfile(res.data);
        setConfiguration(config);
        setProfile(loadedProfile);
        setWarning(res.data?.warning || null);
        applyConfigurationToForm(config);
      } catch (err) {
        if (!isMounted) return;

        if ([404, 204].includes(err?.response?.status)) {
          setConfiguration(null);
          setProfile(null);
          setWarning(null);
          return;
        }

        showErrorToast(getErrorMessage(err, "Failed to load enterprise profile"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [apiPaths.current, isSuperAdmin, normalizedEnterpriseId]);

  const submitGenerate = async (forceRegenerate) => {
    if (!validateGenerateForm()) return;

    setIsGenerating(true);
    setWarning(null);
    setForm((prev) => ({ ...prev, force_regenerate: Boolean(forceRegenerate) }));

    try {
      const res = await api.post(apiPaths.generate, buildGeneratePayload(forceRegenerate));
      const config = readConfiguration(res.data);
      const generatedProfile = readProfile(res.data);

      setConfiguration(config);
      setProfile(generatedProfile);
      applyConfigurationToForm(config);
      showSuccessToast(
        forceRegenerate
          ? "Enterprise profile regenerated successfully"
          : "Enterprise profile generated successfully",
      );
    } catch (err) {
      showErrorToast(getErrorMessage(err, "Failed to generate enterprise profile"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!validateSuperAdminEnterprise()) return;

    setIsDeleting(true);
    setWarning(null);
    try {
      const res = await api.delete(apiPaths.delete);
      const config = readConfiguration(res.data);
      setConfiguration(config);
      setProfile(null);
      applyConfigurationToForm(config);
      showSuccessToast("Enterprise profile deleted successfully");
    } catch (err) {
      showErrorToast(getErrorMessage(err, "Failed to delete enterprise profile"));
    } finally {
      setIsDeleting(false);
    }
  };

  const sections = profile?.sections && typeof profile.sections === "object"
    ? profile.sections
    : {};
  const orderedSectionKeys = [
    ...expectedSectionKeys,
    ...Object.keys(sections).filter((key) => !expectedSectionKeys.includes(key)),
  ];

  return (
    <>
      <div
        className="section-header d-flex align-items-center gap-2 mb-3"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex align-items-center">
          <div className="section-badge me-2">{sectionNumber}</div>
          <h5 className="section-title mb-0">Enterprise Profile</h5>
        </div>
        <i className={`bi ${open ? "bi-caret-up-fill" : "bi-caret-down-fill"}`}></i>
      </div>

      {open && (
        <div className="mb-4 enterprise-profile-section">
          {isSuperAdmin && (
            <div className="refresh-prompts mb-2">
              Enterprise: {enterpriseName || normalizedEnterpriseId || "Not selected"}
            </div>
          )}

          {isLoading ? (
            <div className="refresh-prompts fst-italic">Loading enterprise profile...</div>
          ) : (
            <>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label>
                    Official Website
                    <sup className="text-danger ms-2 fw-bolder">*</sup>
                  </label>
                  <input
                    className="form-control"
                    value={form.website}
                    onChange={updateField("website")}
                    placeholder="example.com"
                  />
                </div>
                <div className="col-md-4">
                  <label>
                    Company Name
                    <sup className="text-danger ms-2 fw-bolder">*</sup>
                  </label>
                  <input
                    className="form-control"
                    value={form.company_name}
                    onChange={updateField("company_name")}
                  />
                </div>
                <div className="col-md-4">
                  <label>Model</label>
                  <input
                    className="form-control"
                    value={form.model}
                    onChange={updateField("model")}
                  />
                </div>
                <div className="col-md-4">
                  <label>Maximum Pages</label>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    max="50"
                    value={form.max_pages}
                    onChange={updateField("max_pages")}
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      id={`enterprise-profile-news-${sectionNumber}`}
                      className="form-check-input"
                      type="checkbox"
                      checked={Boolean(form.include_news)}
                      onChange={updateField("include_news")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`enterprise-profile-news-${sectionNumber}`}
                    >
                      Include Latest News
                    </label>
                  </div>
                </div>
                {/* Force Regenerate is controlled by the Regenerate Enterprise Profile button.
                <div className="col-md-4 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      id={`enterprise-profile-force-${sectionNumber}`}
                      className="form-check-input"
                      type="checkbox"
                      checked={Boolean(form.force_regenerate)}
                      onChange={updateField("force_regenerate")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`enterprise-profile-force-${sectionNumber}`}
                    >
                      Force Regenerate
                    </label>
                  </div>
                </div>
                */}
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  className="custom-btn"
                  onClick={() => submitGenerate(false)}
                  disabled={isGenerating || isDeleting}
                >
                  {isGenerating && !form.force_regenerate
                    ? "Generating..."
                    : "Generate Enterprise Profile"}
                </button>
                <button
                  type="button"
                  className="cancle-btn"
                  onClick={() => submitGenerate(true)}
                  disabled={isGenerating || isDeleting}
                >
                  {isGenerating && form.force_regenerate
                    ? "Regenerating..."
                    : "Regenerate Enterprise Profile"}
                </button>
                <button
                  type="button"
                  className="cancle-btn"
                  onClick={handleDelete}
                  disabled={isGenerating || isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete Enterprise Profile"}
                </button>
              </div>

              {warning && (
                <div className="alert alert-warning py-2" role="alert">
                  {warning.message || "Generated profile is not currently available."}
                </div>
              )}

              {/* Configuration display is hidden for now.
              <div className="mb-3">
                <h6 className="fw-semibold">Configuration</h6>
                <div className="row g-2">
                  <div className="col-md-4">
                    <strong>Official Website:</strong> {configuration?.website || form.website || "Not configured"}
                  </div>
                  <div className="col-md-4">
                    <strong>Company Name:</strong> {configuration?.company_name || form.company_name || "Not configured"}
                  </div>
                  <div className="col-md-4">
                    <strong>Requested Model:</strong> {configuration?.model || form.model}
                  </div>
                  <div className="col-md-4">
                    <strong>Maximum Pages:</strong> {configuration?.max_pages || form.max_pages}
                  </div>
                  <div className="col-md-4">
                    <strong>Include Latest News:</strong>{" "}
                    {configuration?.include_news ?? form.include_news ? "Yes" : "No"}
                  </div>
                  <div className="col-md-4">
                    <strong>Generation Status:</strong>{" "}
                    {configuration?.generation_status || "not_generated"}
                  </div>
                  <div className="col-md-4">
                    <strong>Last Generated At:</strong>{" "}
                    {configuration?.last_generated_at || "Not generated"}
                  </div>
                  <div className="col-md-8">
                    <strong>Last Error:</strong>{" "}
                    {configuration?.last_generation_error_message || "None"}
                  </div>
                </div>
              </div>
              */}

              <div className="mb-2">
                <h6 className="fw-semibold">Generated Profile</h6>
                {profile ? (
                  <>
                    <div className="row g-2 mb-3">
                      <div className="col-md-4">
                        <strong>Company Name:</strong> {profile.company_name || "Not publicly available"}
                      </div>
                      <div className="col-md-4">
                        <strong>Website:</strong> {profile.website || "Not publicly available"}
                      </div>
                      {/* Status is hidden for now.
                      <div className="col-md-4">
                        <strong>Status:</strong> {profile.status || "Not publicly available"}
                      </div>
                      */}
                      <div className="col-md-4">
                        <strong>Word Count:</strong> {profile.word_count ?? "Not publicly available"}
                      </div>
                      {/* Generated Model is hidden for now.
                      <div className="col-md-4">
                        <strong>Generated Model:</strong> {profile.model || "Not publicly available"}
                      </div>
                      */}
                      <div className="col-md-4">
                        <strong>Source Count:</strong> {profile.source_count ?? "Not publicly available"}
                      </div>
                      {/* Created At is hidden for now.
                      <div className="col-md-4">
                        <strong>Created At:</strong> {profile.created_at || "Not publicly available"}
                      </div>
                      */}
                      {/* Updated At is hidden for now.
                      <div className="col-md-4">
                        <strong>Updated At:</strong> {profile.updated_at || "Not publicly available"}
                      </div>
                      */}
                    </div>

                    <div className="mb-3">
                      <strong>Summary:</strong>
                      <div className="generated-profile-summary">
                        {profile.summary || "Not publicly available"}
                      </div>
                    </div>

                    {/* Detailed profile data is hidden for now.
                    <div className="d-flex flex-column gap-2 mb-3">
                      {orderedSectionKeys.map((key) => (
                        <div key={key} className="border rounded p-2">
                          <strong>{formatLabel(key)}</strong>
                          <div className="mt-1">{renderValue(sections[key])}</div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <strong>Sources</strong>
                      {Array.isArray(profile.sources) && profile.sources.length ? (
                        <ul className="mb-0 ps-3">
                          {profile.sources.map((source, index) => (
                            <li key={`${index}-${renderInlineValue(source).slice(0, 20)}`}>
                              {typeof source === "object"
                                ? [
                                    source.title,
                                    source.url,
                                    source.snippet || source.description,
                                  ]
                                    .filter(Boolean)
                                    .join(" - ")
                                : String(source)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div>Not publicly available</div>
                      )}
                    </div>
                    */}
                  </>
                ) : (
                  <div className="refresh-prompts fst-italic">
                    No generated enterprise profile available.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
